/**
 * BattleMap Service
 *
 * Complete backend integration for TabletopEditor.
 * All operations go through backend API - no local file operations.
 * Workflow:
 * 1. Import PNG → Upload to backend → Backend creates template → Return map data
 * 2. Save changes → Embed metadata in PNG → Upload to backend → Update template
 * 3. Load from library → Download PNG from backend → Extract metadata → Load in editor
 * 4. Export → Download PNG from backend with current state
 */

import { TacticalMapTemplatesGameApi } from '@/api/backendService'
import { embedMetadataInPNG, extractMetadataFromPNG } from '@/utils/pngMetadata.js'

class BattleMapService {
  constructor() {
    // Use the already instantiated API from backendService
    this.api = TacticalMapTemplatesGameApi
  }

  /**
   * List all available templates from backend
   * @param {Object} filters - Optional filters (category, difficulty, published, search)
   * @returns {Promise<Array>} List of templates
   */
  async listTemplates(filters = {}) {
    try {
      // API signature: tacticalmapTemplatesList(canonical?, category?, difficultyRating?, ordering?, published?, search?)
      const response = await this.api.tacticalmapTemplatesList(
        filters.canonical,           // canonical filter
        filters.category,            // category filter
        filters.difficulty,          // difficulty_rating filter (note: backend uses difficulty_rating)
        filters.ordering,            // ordering
        filters.published,           // published filter
        filters.search               // search term
      )
      return response.data.results || response.data
    } catch (error) {
      console.error('[BattleMapService] Error listing templates:', error)
      throw new Error('Failed to load map templates')
    }
  }

  /**
   * Get a specific template by ID with full map data
   * @param {string} id - Template ID
   * @returns {Promise<Object>} Template with map_data
   */
  async getTemplate(id) {
    try {
      const response = await this.api.tacticalmapTemplatesRetrieve(id)
      return response.data
    } catch (error) {
      console.error('[BattleMapService] Error loading template:', error)
      throw new Error('Failed to load map template')
    }
  }

  /**
   * Upload a PNG file to backend for import
   * Backend will extract metadata and create template
   * @param {File} file - PNG file with embedded TabletopMapData
   * @param {Object} options - Import options
   * @returns {Promise<Object>} Created template
   */
  async importToBackend(file, options = {}) {
    try {
      // API signature: tacticalmapTemplatesImportPngCreate(file?, overrideName?, overridePublished?, options?)
      const response = await this.api.tacticalmapTemplatesImportPngCreate(
        file,                        // File object
        options.name || null,        // override_name
        options.published || false   // override_published
      )

      // Backend returns { template: {...}, warnings: [...] }
      if (response.data.template) {
        console.log('[BattleMapService] Template imported successfully:', response.data.template.id)
        if (response.data.warnings?.length > 0) {
          console.warn('[BattleMapService] Import warnings:', response.data.warnings)
        }
        return response.data.template
      }

      return response.data
    } catch (error) {
      console.error('[BattleMapService] Error importing PNG to backend:', error)

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(', '))
      }

      if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      }

      throw new Error('Failed to import map to backend')
    }
  }

  /**
   * Validate a PNG file without creating a template
   * @param {File} file - PNG file to validate
   * @returns {Promise<Object>} Validation result { valid: boolean, errors?: array, warnings?: array }
   */
  async validatePNG(file) {
    try {
      // API signature: tacticalmapTemplatesValidatePngCreate(file?, options?)
      const response = await this.api.tacticalmapTemplatesValidatePngCreate(file)
      return response.data
    } catch (error) {
      console.error('[BattleMapService] Error validating PNG:', error)

      if (error.response?.data?.errors) {
        return {
          valid: false,
          errors: error.response.data.errors
        }
      }

      throw new Error('Failed to validate PNG')
    }
  }

  /**
   * Download a template as PNG with embedded metadata from backend
   * @param {string} id - Template ID
   * @param {string} filename - Optional filename
   * @returns {Promise<void>} Downloads the file
   */
  async exportFromBackend(id, filename) {
    try {
      const response = await this.api.tacticalmapTemplatesExportPngRetrieve(id, {
        responseType: 'blob'
      })

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url

      // Use provided filename or extract from response headers
      if (filename) {
        link.download = filename
      } else {
        const contentDisposition = response.headers['content-disposition']
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
          if (filenameMatch) {
            link.download = filenameMatch[1]
          }
        }

        if (!link.download) {
          link.download = `battle_map_${id}.png`
        }
      }

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      console.log('[BattleMapService] Template exported:', filename || link.download)
    } catch (error) {
      console.error('[BattleMapService] Error exporting PNG from backend:', error)
      throw new Error('Failed to export map as PNG')
    }
  }

  /**
   * Create new template in backend from map data
   * Embeds metadata in PNG, then uploads to backend
   * @param {Object} mapData - Complete map data (v2.0 format)
   * @param {string} backgroundImageDataURL - Background image as data URL
   * @param {Object} options - Additional options (published, etc.)
   * @returns {Promise<Object>} Created template with id
   */
  async createTemplate(mapData, backgroundImageDataURL, options = {}) {
    try {
      console.log('[BattleMapService] Creating new template...')

      // Prepare clean map data (no backgroundImage in metadata)
      const { backgroundImage, ...cleanMapData } = mapData

      // Embed metadata in PNG
      const pngBlob = await embedMetadataInPNG(backgroundImageDataURL, cleanMapData)

      // Convert blob to File
      const fileName = `${cleanMapData.metadata.name.replace(/\s+/g, '_')}.png`
      const file = new File([pngBlob], fileName, { type: 'image/png' })

      // Upload to backend
      const template = await this.importToBackend(file, {
        published: options.published || false
      })

      console.log('[BattleMapService] Template created:', template.id)
      return template
    } catch (error) {
      console.error('[BattleMapService] Error creating template:', error)
      throw error
    }
  }

  /**
   * Update existing template in backend
   * Uses PATCH to update map_data without changing template ID
   * @param {string} templateId - Template ID to update
   * @param {Object} mapData - Updated map data
   * @param {string} backgroundImageDataURL - Background image as data URL (not used for now)
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Updated template
   */
  async updateTemplate(templateId, mapData, backgroundImageDataURL, options = {}) {
    try {
      console.log('[BattleMapService] Updating template:', templateId)

      // Prepare clean map data (no backgroundImage in metadata)
      const { backgroundImage, _templateId, _templateVersion, _templateName, ...cleanMapData } = mapData

      // Update template using PATCH endpoint
      // API signature: tacticalmapTemplatesPartialUpdate(id, data?)
      const response = await this.api.tacticalmapTemplatesPartialUpdate(
        templateId,
        {
          name: cleanMapData.metadata?.name,
          description: cleanMapData.metadata?.description,
          map_data: cleanMapData,
          published: options.published
        }
      )

      console.log('[BattleMapService] Template updated successfully:', templateId)
      return response.data
    } catch (error) {
      console.error('[BattleMapService] Error updating template:', error)
      throw error
    }
  }

  /**
   * Load template from backend and extract map data
   * Fetches background image and converts to data URL for canvas
   * @param {string} templateId - Template ID to load
   * @returns {Promise<Object>} Map data with backgroundImage and template metadata
   */
  async loadTemplate(templateId) {
    try {
      console.log('[BattleMapService] Loading template:', templateId)

      // Get template metadata first
      const template = await this.getTemplate(templateId)

      // Fetch background image and convert to data URL
      let backgroundImageDataURL = null
      if (template.background_image) {
        console.log('[BattleMapService] Fetching background image:', template.background_image)
        backgroundImageDataURL = await this._fetchImageAsDataURL(template.background_image)
      }

      // Compose map data with background image
      const mapData = {
        ...template.map_data,
        backgroundImage: backgroundImageDataURL, // Add background image for canvas
        _templateId: template.id,
        _templateVersion: template.template_version,
        _templateName: template.name
      }

      console.log('[BattleMapService] Template loaded:', mapData.metadata?.name)
      return mapData
    } catch (error) {
      console.error('[BattleMapService] Error loading template:', error)
      throw error
    }
  }

  /**
   * Fetch image from URL and convert to data URL
   * @param {string} imageUrl - URL to image
   * @returns {Promise<string>} Data URL (base64)
   * @private
   */
  async _fetchImageAsDataURL(imageUrl) {
    try {
      // Fetch image as blob
      const response = await fetch(imageUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`)
      }

      const blob = await response.blob()

      // Convert blob to data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('[BattleMapService] Error fetching image as data URL:', error)
      throw error
    }
  }

  /**
   * Import PNG from user's disk through backend
   * Uploads file, backend creates template, returns map data for editing
   * @param {File} file - PNG file from user's disk
   * @param {Object} options - Import options
   * @returns {Promise<Object>} Map data ready for editor
   */
  async importFromDisk(file, options = {}) {
    try {
      console.log('[BattleMapService] Importing from disk:', file.name)

      // First, extract and validate metadata locally
      const { mapData, imageDataURL, hasMetadata } = await extractMetadataFromPNG(file)

      if (!hasMetadata) {
        throw new Error('PNG file has no embedded map data. Please export from TabletopEditor first.')
      }

      // Upload to backend to create template
      const template = await this.importToBackend(file, options)

      // Return map data with background image for editor
      const editorMapData = {
        ...mapData,
        backgroundImage: imageDataURL,
        _templateId: template.id,
        _templateVersion: template.template_version,
        _templateName: template.name
      }

      console.log('[BattleMapService] Import complete, template created:', template.id)
      return editorMapData
    } catch (error) {
      console.error('[BattleMapService] Error importing from disk:', error)
      throw error
    }
  }

  /**
   * Export template as PNG file download
   * Downloads from backend with embedded metadata
   * @param {string} templateId - Template ID to export
   * @param {string} customFilename - Optional custom filename
   * @returns {Promise<void>} Downloads file to user's disk
   */
  async exportToDisk(templateId, customFilename) {
    try {
      console.log('[BattleMapService] Exporting to disk:', templateId)
      await this.exportFromBackend(templateId, customFilename)
      console.log('[BattleMapService] Export complete')
    } catch (error) {
      console.error('[BattleMapService] Error exporting to disk:', error)
      throw error
    }
  }

  /**
   * Delete a template from backend
   * @param {string} id - Template ID
   * @returns {Promise<void>}
   */
  async deleteTemplate(id) {
    try {
      await this.api.tacticalmapTemplatesDestroy(id)
      console.log('[BattleMapService] Template deleted:', id)
    } catch (error) {
      console.error('[BattleMapService] Error deleting template:', error)
      throw new Error('Failed to delete template')
    }
  }
}

// Export singleton instance
export default new BattleMapService()
